import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Search } from "lucide-react";

export default function PatientRewards() {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const load = async () => {
    const [r, t] = await Promise.all([api.get("/patient/rewards"), api.get(`/tenants/by-slug/${slug}`)]);
    setRewards(r.data);
    setTenant(t.data);
  };
  useEffect(() => { load(); }, [slug]);

  const categories = ["all", ...Array.from(new Set(rewards.map((r) => r.category)))];

  const filtered = rewards.filter((r) =>
    (cat === "all" || r.category === cat) &&
    (q === "" || r.title.toLowerCase().includes(q.toLowerCase()))
  );

  const redeem = async (id) => {
    try {
      await api.post(`/patient/redeem/${id}`);
      toast.success("Reward redeemed!");
      await refresh();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not redeem"));
    }
  };

  return (
    <div className="min-h-screen bg-mr-bg">
      <TopBar tenant={tenant} links={[
        { to: `/c/${slug}/dashboard`, label: "Dashboard" },
        { to: `/c/${slug}/rewards`, label: "Rewards" },
        { to: `/c/${slug}/family`, label: "Family" },
        { to: `/c/${slug}/refer`, label: "Refer" },
        { to: `/c/${slug}/profile`, label: "Profile" },
      ]} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="label-eyebrow text-mr-accent">{user?.points?.toLocaleString()} points available</span>
            <h1 className="font-display text-4xl font-bold text-mr-primary mt-2">Rewards catalogue</h1>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-mr-secondary" />
            <Input data-testid="rewards-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rewards…" className="pl-9 w-64 rounded-full bg-white border-mr h-10" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`rewards-filter-${c}`}
              onClick={() => setCat(c)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${cat === c ? "bg-mr-primary text-white" : "bg-white border border-mr text-mr-secondary hover:text-mr-primary"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r, i) => {
            const can = (user?.points || 0) >= r.cost;
            return (
              <div key={r.id} data-testid={`reward-card-${r.id}`} className="bg-white border border-mr rounded-2xl p-6 flex flex-col animate-fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><Gift className="h-6 w-6 text-mr-accent" /></div>
                  <Badge variant="outline" className="rounded-full border-mr text-mr-secondary">{r.category}</Badge>
                </div>
                <h3 className="font-display text-lg font-semibold text-mr-primary">{r.title}</h3>
                <p className="text-mr-secondary text-sm mt-2 flex-1">{r.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <p className="font-display text-2xl font-bold text-mr-primary">{r.cost.toLocaleString()} <span className="text-xs font-normal text-mr-secondary">pts</span></p>
                  <Button
                    data-testid={`reward-redeem-${r.id}`}
                    size="sm"
                    disabled={!can}
                    onClick={() => redeem(r.id)}
                    className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)] disabled:opacity-40"
                  >
                    {can ? "Redeem" : `Need ${(r.cost - (user?.points || 0)).toLocaleString()}`}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && <p className="text-center text-mr-secondary py-12">No rewards match your search.</p>}
      </div>
    </div>
  );
}
